import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { validateAssayAnalytes } from '@mariana/labos-client';
import { postAssay, apiErrorMessage } from '../../api/limsClient';
import { Breadcrumb } from '../Breadcrumb';
import { definitionFromRows, workedExamplesPass, type AnalyteRow } from '../../utils/assayDefinition';
import { AnalytesTable } from './AnalytesTable';
import { AssayMetadataFields } from './AssayMetadataPanel';
import { metadataAttributes, type AssayMetadataValues } from '../../utils/assayMetadata';
import { exampleAssay } from '../../utils/assayHelp';
import './AssayDefinition.css';

const EMPTY: AssayMetadataValues = { title: '', sampleTypes: '', capture: 'manual', method: '', description: '', sopUrl: '' };

/**
 * Self-serve creation of a manual or upload assay. The row is created as a
 * draft with the legacy flags off; promoting it to active is a separate
 * click on the detail page's Definition tab.
 */
export const NewAssayPage: React.FC = () => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState<AssayMetadataValues>(EMPTY);
  const [rows, setRows] = useState<AnalyteRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const titleOk = meta.title.trim().length > 0;
  const examplesOk = workedExamplesPass(rows, true);
  const definition = rows.length > 0 ? definitionFromRows(rows) : null;
  const definitionCheck = definition ? validateAssayAnalytes(definition) : null;
  const definitionError = definitionCheck && 'error' in definitionCheck ? definitionCheck.error : null;
  const canSave = titleOk && examplesOk && !definitionError && !saving;
  const untouched = !meta.title.trim() && rows.length === 0;
  const fillExample = () => {
    const ex = exampleAssay();
    setMeta(ex.meta);
    setRows(ex.rows);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const attributes: Record<string, unknown> = {
        ...metadataAttributes(meta, { includeTitle: true, forCreate: true }),
        state: 'draft',
        active: false,
        visible: false,
      };
      if (definition) attributes.analytes = definition;
      const id = await postAssay(attributes);
      navigate(`/assay/${id}?tab=definition`);
    } catch (err) {
      setError(apiErrorMessage(err));
      setSaving(false);
    }
  };

  return (
    <div className="assay-page">
      <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: 'Assays', path: '/assay' }, { label: 'New assay' }]} />
      <h2 className="assay-page-title">New assay</h2>
      <p className="assay-intro">
        Describe the method, then add one analyte row per value a test of it reports, reads from the bench or computes.
        It is saved as a draft, hidden from Capital OS until an admin promotes it, so nothing here can break a running assay.
        New to this? Read <Link to="/assay/guide">how to define an assay</Link>
        {untouched && <>, or <button type="button" className="assay-link-btn" onClick={fillExample}>fill the form with a real example</button> and change it into yours</>}.
      </p>

      <div className="assay-section">
        <AssayMetadataFields values={meta} onChange={v => { setMeta(v); setError(null); }} creating />
      </div>

      <div className="assay-section">
        <h3 className="assay-section-heading">
          Analytes
          <span className="assay-muted">one row per value the assay reports, reads or computes</span>
        </h3>
        <AnalytesTable rows={rows} onChange={next => { setRows(next); setError(null); }} requireAllExamples />
        {definitionError && rows.length > 0 && <div className="assay-note" style={{ marginTop: '0.375rem' }}>{definitionError}</div>}
      </div>

      <div className="assay-actions">
        <button type="button" className="assay-btn primary" disabled={!canSave} onClick={save}
          title={!titleOk ? 'A title is required' : !examplesOk ? 'Every derived value needs a passing worked example' : definitionError ?? undefined}>
          {saving ? 'Creating…' : 'Create draft assay'}
        </button>
        <button type="button" className="assay-btn" disabled={saving} onClick={() => navigate('/assay')}>Cancel</button>
        <span className="assay-muted">Created as a draft (active and visible off). An admin promotes it on the Definition tab once it is right.</span>
      </div>
      {error && <div className="assay-error" role="alert">{error}</div>}
    </div>
  );
};
